import { mapRouteTypeToPath } from "@deskpro-product/messenger-api-schemas/messenger/config/routes";
import type { GoTo } from "@deskpro-product/messenger-api-schemas/messenger-init/types";
import type { ToOptions } from "@tanstack/react-router";
import type { LocalStorageState } from "@/lib/types/localStorageTypes";
import type { AllActiveConversations } from "../contexts/DeviceContext";
import type { MessengerConfig } from "@deskpro-product/messenger-api-schemas/messenger/config";

export default function getInitialPageForUser({
  config,
  goTo,
  tenantId,
  messengerId,
  hostConfig,
  locale,
  lastVisitedTab,
  activeConversations,
}: {
  config?: MessengerConfig;
  goTo?: Exclude<GoTo, { type: "locale" }>;
  tenantId: string;
  messengerId: string;
  hostConfig: string;
  locale: string;
  lastVisitedTab: LocalStorageState["lastVisited"];
  activeConversations: AllActiveConversations["activeConversations"];
}): ToOptions {
  if (goTo) {
    switch (goTo.type) {
      case "chat":
        return {
          to: "/$tenantId/$messengerId/$hostConfig/$locale/messages/$id",
          params: { tenantId, messengerId, hostConfig, locale, id: goTo.id },
        };
      case "chat.new": {
        return {
          to: "/$tenantId/$messengerId/$hostConfig/$locale/messages/new",
          params: { tenantId, messengerId, hostConfig, locale },
          search: { flowId: 1, ...goTo.metaData },
        };
      }
      case "tab": {
        return {
          to: `/$tenantId/$messengerId/$hostConfig/$locale/${goTo.tab}`,
          params: { tenantId, messengerId, hostConfig, locale, tab: goTo.tab },
        };
      }
      case "path": {
        const basePath = new RegExp(`^${import.meta.env.VITE_BASE_PATH}`);
        return { to: goTo.path.replace(basePath, "") as ToOptions["to"] }; // To should also accept a string
      }
      default:
        goTo satisfies never;
    }
  }

  if (
    activeConversations.length > 0 &&
    lastVisitedTab?.tab === "conversation" &&
    lastVisitedTab.id === activeConversations[0].id
  ) {
    return {
      to: "/$tenantId/$messengerId/$hostConfig/$locale/messages/$id",
      params: { tenantId, messengerId, hostConfig, locale, id: activeConversations[0].id },
    };
  }

  if (lastVisitedTab) {
    switch (lastVisitedTab.tab) {
      case "conversation":
        return {
          to: "/$tenantId/$messengerId/$hostConfig/$locale/messages/$id",
          params: { tenantId, messengerId, hostConfig, locale, id: lastVisitedTab.id },
        };
      default:
        if (config?.mode === "normal") {
          return {
            to: `/$tenantId/$messengerId/$hostConfig/$locale/${lastVisitedTab.tab}`,
            params: { tenantId, messengerId, hostConfig, locale },
          };
        }
    }
  }
  const defaultTab = (config?.mode === "normal" && config.routes?.[0]?.type) || "MessagesRoute";
  return {
    to: `/$tenantId/$messengerId/$hostConfig/$locale/${mapRouteTypeToPath[defaultTab]}`,
    params: { tenantId, messengerId, hostConfig, locale },
  };
}
